<template>
  <!-- Brands Table -->
  <div class="w-full">
    <!-- Table Header -->
    <div class="brand-grid-template w-full bg-neutral-10 rounded-lg">
      <BaseText type="body" size="sm" class="text-text-normal pl-12 py-2 pr-4">
        {{ brandTotal.toLocaleString() }} Brands
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal px-3 py-2">
        Socials
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal px-3 py-2">
        Industry
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal px-3 py-2">
        Business Model
      </BaseText>
    </div>
    <!-- Loading state -->
    <div v-if="loadingBrands" class="w-full flex justify-center mt-24">
      <BaseLoadingLogo />
    </div>
    <!-- No favorited brands state -->
    <div v-else-if="favoriteFilter && !brands.length" class="w-full flex flex-col items-center pt-24">
      <NoBrandFavoritesAnimatedIcon :width="48" :height="48" />
      <BaseText type="label" size="md" class="text-text-muted mt-5">
        Still waiting for your top picks!
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-muted mt-2 text-center">
        Looks like your heart is still up for grabs! Start saving and
        <br/>show some love to those top brands.
      </BaseText>
    </div>
    <!-- No brand results state -->
    <div v-else-if="!brands.length" class="w-full flex flex-col items-center pt-24">
      <NoBrandsAnimatedIcon :width="96" :height="96" />
      <BaseText type="label" size="md" class="relative bottom-4 text-text-muted mt-5">
        No results found!
      </BaseText>
      <BaseText type="body" size="sm" class="relative bottom-4 text-text-muted mt-2 text-center">
        We couldn't find any matches. Try tweaking your search or
        <br/>start saving ads to see results.
      </BaseText>
    </div>
    <!-- Brands Table -->
    <div v-else class="w-full flex flex-col gap-1 mt-1">
      <router-link v-for="(brand, index) in brandRows" :key="index"
      class="brand-grid-template rounded-lg transition-colors hover:bg-neutral-25"
      :to="{ name: 'BrandView', params: { id: brand.id } }">
        <!-- Brand name & avatar -->
        <div class="flex items-center gap-3 p-2 min-w-0">
          <BrandAvatar :avatar-src="brand.avatar" :brand-name="brand.name" :width="28" :height="28" class="rounded-full" />
          <BaseText type="label" size="sm" class="text-text-muted truncate">
            {{ brand.name }}
          </BaseText>
        </div>
        <!-- Brand socials -->
        <div class="px-3 py-2.5">
          <div v-if="brand.platforms.length" class="flex items-center gap-2">
            <component :is="brand.platforms[0].icon" :width="16" :height="16" />
            <BaseText type="body" size="sm" class="text-text-normal capitalize truncate py-0.5">
              {{ brand.platforms[0].name }}
            </BaseText>
            <!-- Additional platforms indicator -->
            <div v-if="brand.platforms.length > 1" class="relative px-1 py-0.5 border border-border-normal bg-white
            transition-colors hover:bg-neutral-50 hover:border-transparent" style="border-radius: 4px"
            @mouseenter="hoveredPlatformTooltip = brand.id" @mouseleave="hoveredPlatformTooltip = null">
              <BaseText type="label" size="xs" class="text-text-normal">
                +{{ brand.platforms.length - 1 }}
              </BaseText>
              <!-- Rendered tooltip on hover -->
              <transition>
                <div v-if="hoveredPlatformTooltip === brand.id" class="pb-1 platform-tooltip">
                  <div class="flex flex-col gap-1 px-1 py-1.5 rounded-xl bg-neutral-malpha-25"
                  style="width: 180px;" @mouseenter="hoveredPlatformTooltip = null">
                    <div v-for="platform in brand.platforms.slice(1)"
                    :key="`brand-platform-${platform.key}`" class="w-full flex items-center gap-2 px-1.5 py-1">
                      <div class="p-0.5">
                        <component :is="platform.icon" :width="16" :height="16" />
                      </div>
                      <BaseText type="body" size="sm" class="text-white capitalize">
                        {{ platform.name }}
                      </BaseText>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
        <!-- Brand industry -->
        <div class="p-3">
          <BaseText v-if="brand.industry" type="body" size="sm" class="text-text-normal truncate">
            {{ brand.industry }}
          </BaseText>
        </div>
        <!-- Brand business model -->
        <div class="p-3">
          <BaseText v-if="brand.businessModel" type="body" size="sm" class="text-text-normal truncate">
            {{ brand.businessModel }}
          </BaseText>
        </div>
        <!-- Buttons/Actions -->
        <div class="flex items-center justify-end px-3 py-1.5">
          <!-- Favorite Button -->
          <button class="group w-8 h-8 rounded-md transition-colors hover:bg-neutral-50"
          @click.stop.prevent="$emit('favorite', brand.id)">
            <FavoriteIcon no-hover :active="brand.isFavorited"
            class="text-icon-normal transition-colors group-hover:text-icon-muted" />
          </button>
          <!-- Ad Library Button -->
          <div class="w-px h-4 mx-2 bg-border-normal" />
          <button class="group flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md"
          :class="brand.adLibraryId ? 'transition-colors hover:bg-neutral-50' : 'cursor-default opacity-30'"
          @click.stop.prevent="() => {goToAdLibrary(brand)}" :disabled="!brand.adLibraryId">
            <BaseText type="label" size="sm" class="text-text-muted">
              Ad Library
            </BaseText>
            <OpenExternalIcon class="text-icon-normal" :class="{'transition-colors group-hover:text-icon-muted': brand.adLibraryId}" />
          </button>
          <!-- Swipe File brands are Spyder brands, so this opens the brand's Spyder page -->
          <div class="w-px h-4 mx-2 bg-border-normal" />
          <button class="group flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md transition-colors hover:bg-neutral-50"
          @click.stop.prevent="viewSpyderBrand(brand)">
            <BaseText type="label" size="sm" class="text-text-muted">
              Spyder
            </BaseText>
            <OpenExternalIcon class="text-icon-normal transition-colors group-hover:text-icon-muted" />
          </button>
        </div>
      </router-link>
    </div>
    <!-- Infinite loading -->
    <infinite-loading
      v-if="brands.length"
      :identifier="infiniteId"
      @infinite="handleInfiniteLoading"
    >
      <div slot="spinner">
        <BaseLoadingLogo :margin="2" />
      </div>
      <div slot="no-more" />
      <div slot="no-results" />
    </infinite-loading>
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import { mapGetters } from 'vuex'
import { getSocialPlatformIcon, getSocialPlatformName } from '@/utils/spyder/brandSocialsUtils'
import BrandAvatar from './BrandAvatar.vue'

// Icons
import FavoriteIcon from '../globals/Icons/FavoriteIcon.vue'
import NoBrandFavoritesAnimatedIcon from '../globals/Icons/NoBrandFavoritesAnimatedIcon.vue'
import NoBrandsAnimatedIcon from '../globals/Icons/NoBrandsAnimatedIcon.vue'
import OpenExternalIcon from '../globals/Icons/OpenExternalIcon.vue'

export default {
  name: 'BrandsList',
  components: {
    BrandAvatar,
    FavoriteIcon,
    NoBrandFavoritesAnimatedIcon,
    NoBrandsAnimatedIcon,
    OpenExternalIcon,
    InfiniteLoading
  },
  props: {
    brands: {
      type: Array,
      required: true
    },
    brandTotal: {
      type: [String, Number],
      default: ''
    },
    loadingBrands: {
      type: Boolean,
      default: false
    },
    favoriteFilter: {
      type: Boolean,
      default: false
    },
    infiniteId: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      hoveredPlatformTooltip: null
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getIsSubscriptionActive']),
    // Built once per brands change, not on every re-render (e.g. hovering a platforms tooltip)
    brandRows () {
      return this.brands.map(brand => ({
        ...brand,
        platforms: this.getBrandPlatforms(brand)
      }))
    }
  },
  watch: {
    getIsSubscriptionActive (newVal, oldVal) {
      if (newVal && !oldVal) {
        this.infiniteId += 1
      }
    }
  },
  methods: {
    handleInfiniteLoading ($state) {
      if (!this.getIsSubscriptionActive) {
        this.$showAlert({ type: 'info', message: 'Please renew your subscription to continue using this feature.' })
        $state.complete()
        return
      }

      this.$emit('get-more-brands', $state)
    },
    goToAdLibrary (brand) {
      if (!brand.adLibraryId) return
      window.open(`https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=ALL&view_all_page_id=${brand.adLibraryId}`, '_blank')
    },
    viewSpyderBrand (brand) {
      this.$router.push({ name: 'SpyderView', params: { id: brand.id } })
    },

    // ================================================================================
    // ================================== UI METHODS ==================================
    // ================================================================================

    // Platforms listed in the socials column: the brand's social networks
    getBrandPlatforms (brand) {
      return (brand.socialNetworks || []).map(network => ({
        key: network,
        icon: getSocialPlatformIcon(network),
        name: getSocialPlatformName(network)
      }))
    }
  }
}
</script>

<style scoped>
.platform-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 40001; /* So it renders above filter top bar */
}

/* Grid rendering classes with media queries - columns get removed at smaller widths */
/* Order of removal: Socials -> Industry -> Business Model */
.brand-grid-template {
  display: grid;
  grid-template-columns: 1fr 172px 200px 164px 312px;
}
@media (max-width: 1480px) {
  .brand-grid-template > :nth-child(2) {
    display: none;
  }
  .brand-grid-template {
    grid-template-columns: 1fr 156px 156px 312px;
  }
}
@media (max-width: 1325px) {
  .brand-grid-template > :nth-child(3) {
    display: none;
  }
  .brand-grid-template {
    grid-template-columns: 1fr 156px 312px;
  }
}
@media (max-width: 1175px) {
  .brand-grid-template > :nth-child(4) {
    display: none;
  }
  .brand-grid-template {
    grid-template-columns: 1fr 312px;
  }
}

/* Vue <transition> classes */
.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>
